<script lang="ts">
import {defineComponent} from "vue";
import Icon from "@/components/Icon.vue";
import CloseIcon from "@/assets/icons/CloseIcon.vue";

export default defineComponent({
  name: "Modal",
  components: {CloseIcon, Icon},

  props: {
    title: {
      type: String,
      default: ""
    },

    autoHeight: {
      type: Boolean,
      default: false
    },

    size: {
      type: String,
      default: "xl",
      validator: value => ["sm", "md", "xl", "xxl"].includes(value as string)
    },

    bodyClass: {
      type: String,
      default: ""
    }
  },

  data() {
    return {isOpen: false};
  },

  computed: {
    formattedTitle() {
      return this.title.replace(/\n/g, "<br>");
    }
  },

  methods: {
    openModal() {
      this.isOpen = true;

      this.$emit("modal-opened");
    },

    closeModal() {
      this.isOpen = false;

      this.$emit("modal-closed");
      this.$emit("close");
    },

    manualClose() {
      this.isOpen = false;
    },

    handleEscapeKey(event: { key: string; }) {
      if (!this.isOpen) return

      if (event.key === 'Escape') {
        this.closeModal();
      }
    },

    getBodyHeight() {
      const bodyModal = (this.$refs.bodyModal as HTMLElement);
      if (bodyModal) {
        return bodyModal.offsetHeight;
      }
      return 0;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleEscapeKey);
  },

  unmounted() {
    document.removeEventListener('keydown', this.handleEscapeKey);
  }
});
</script>

<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="custom-modal">
      <div class="custom-modal-bg" @click.stop="closeModal()"></div>
      <div class="custom-modal-content" :class="autoHeight ? 'custom-modal-content-auto' : ''"
           :data-modal="`modal-${size}`">
        <div class="custom-modal-header d-flex align-items-center justify-content-between" ref="header">
          <div class="custom-modal-title w-100"
               v-html="formattedTitle"
          />

          <Icon class="icon-close" @click="() => closeModal()">
            <CloseIcon/>
          </Icon>
        </div>
        <div class="custom-modal-body" :class="bodyClass" v-if="$slots.body" ref="bodyModal">
          <slot name="body"/>
        </div>
        <div class="custom-modal-footer" v-if="$slots.footer" ref="footerModal">
          <div class="row g-2">
            <slot name="footer"/>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style>
[data-modal="modal-sm"] {
  --width: 30%;

  .custom-modal-title {
    padding: 30px 45px;

    text-align: center;
    font-size: 20px;
  }

  .custom-modal-header {
    height: 100%;
  }

  .custom-modal-body {
    padding: 25px;
  }

  .icon-close {
    position: absolute;

    right: 10px;
    top: 10px;
  }
}

[data-modal="modal-md"] {
  --width: 30%;

  .custom-modal-header {
    height: 100%;
  }

  .custom-modal-body {
    padding: 25px;
  }

  .icon-close {
    position: absolute;

    right: 10px;
    top: 10px;
  }
}

[data-modal="modal-xl"] {
  --width: 50%;
}

[data-modal="modal-xxl"] {
  --width: 90%;
}

.custom-modal {
  position: fixed;

  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  overflow-x: hidden;
  overflow-y: auto;

  outline: 0;

  z-index: 1000;

  display: flex;

  align-items: center;
  justify-content: center;
}

.custom-modal-bg {
  position: absolute;

  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  background: rgba(11, 20, 23, 0.9);

  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);

  z-index: 1;
}

.custom-modal-content {
  position: relative;

  background: linear-gradient(0deg, rgba(68, 71, 72, 0.15) 0%, rgba(68, 71, 72, 0.075) 100%);

  border: 1px solid rgba(184, 232, 255, 0.15);

  border-radius: 2px;

  width: var(--width);
  height: calc(100% - 200px);
  max-height: calc(100% - 200px);

  display: flex;
  flex-direction: column;

  overflow: hidden;

  z-index: 2;
}

.custom-modal-body {
  flex: 1 1 auto;

  padding: 10px;

  max-height: calc(100vh - 200px);

  overflow-x: hidden;
  overflow-y: auto;
}

.custom-modal-footer {
  padding: 10px;
}

.custom-modal-header {
  height: 35px;
  padding: 10px 5px 10px 10px;
}

.custom-modal-footer,
.custom-modal-header {
  background: linear-gradient(0deg, rgba(68, 71, 72, 0.25) 0%, rgba(68, 71, 72, 0.1875) 100%);

  border-radius: 2px;

  border: 1px solid rgba(184, 232, 255, 0.15);
}

.custom-modal-title {
  font-family: "Artifex CF Demi Bold", serif;
  font-size: 14px;

  color: rgba(255, 255, 255, 1);
}

.custom-modal-content-auto {
  height: auto !important;
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}
</style>
